import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
				destructive:
					"bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
				outline:
					"border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
				secondary:
					"bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
				ghost:
					"hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				xs: "h-4 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
				sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
				md: "h-9 gap-2 rounded-md px-4 has-[>svg]:px-3",
				lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
				icon: "size-9",
			},
			justify: {
				center: "justify-center",
				start: "justify-start",
				end: "justify-end",
				between: "justify-between",
				around: "justify-around",
				evenly: "justify-evenly",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
			justify: "center",
		},
	},
);

function Button({
	className,
	variant,
	size,
	justify,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, justify, className }))}
			{...props}
		/>
	);
}

const imageButtonVariants = cva(
	"inline-flex shrink-0 items-center gap-2 whitespace-nowrap font-medium text-sm outline-none transition-opacity disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
	{
		variants: {
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				xs: "h-4 gap-1.5 px-3 has-[>svg]:px-2.5",
				sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
				md: "h-9 gap-2 px-4 has-[>svg]:px-3",
				lg: "h-10 px-6 has-[>svg]:px-4",
				icon: "size-9",
			},
			justify: {
				center: "justify-center",
				start: "justify-start",
				end: "justify-end",
				between: "justify-between",
				around: "justify-around",
				evenly: "justify-evenly",
			},
		},
		defaultVariants: {
			size: "default",
			justify: "center",
		},
	},
);

function ImageButton({
	className,
	size,
	justify,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof imageButtonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="image-button"
			className={cn(imageButtonVariants({ size, justify, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants, ImageButton, imageButtonVariants };
